import { debounce } from 'lodash';
import * as _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import FuelTags from '@/components/fuel-tags';
import VNAutocomplete from '@/components/VNAutocomplete/VNAutocomplete';
import VNCheckBox from '@/components/VNCheckBox/VNCHeckBox';
import VNDatePicker from '@/components/VNDatePicker/VNDatePicker';
import VNTextField from '@/components/VNTextField/VNTextField';
import type { AccessSchedule } from '@/models/channel';
import { applicationDataSelector } from '@/store/slices/application-data';
import { accessUtils } from '@/utils/accessUtils';
import itemUtils from '@/utils/itemUtils';

import Metadata from './Metadata';

interface Props {
  isMultiple?: boolean;
  hasMov?: boolean;
  isNew?: boolean;
}

function VideoForm({ isMultiple, hasMov = false, isNew = false }: Props) {
  const { ouData, categories } = useSelector(applicationDataSelector);
  const { getValues, setValue, watch } = useFormContext();
  const [originalValues, setOriginalValue] = useState(getValues());

  const [previousTags, setPreviousTags] = useState(getValues('tags') || []);
  // const [previousCommon, setPreviousCommon] = useState(itemUtils.getFullTagsFromCommonMetadata(getValues('common')) || []);

  const tags = watch('tags');
  const common = watch('common');

  const onEmbargoDateChanged = (oldEmbargo: string, newEmbargo: string) => {
    // console.log('onEmbargoDateChanged', { newEmbargo, newEmbargo });
    let newAccessSchedules: AccessSchedule[] = _.cloneDeep(getValues('accessSettings.temporal.accessSchedules'));
    if (newAccessSchedules == null) {
      newAccessSchedules = [];
    }
    newAccessSchedules = accessUtils.ensureEmbargoSet(newAccessSchedules, oldEmbargo, newEmbargo);
    setValue('accessSettings.temporal.accessSchedules', newAccessSchedules);
  };

  const onExpireDateChanged = (oldExpire: string, newExpire: string) => {
    // console.log('onExpireDateChanged', { oldExpire, newExpire });
    let newAccessSchedules: AccessSchedule[] = _.cloneDeep(getValues('accessSettings.temporal.accessSchedules'));
    if (newAccessSchedules == null) {
      newAccessSchedules = [];
    }
    newAccessSchedules = accessUtils.ensureExpireSet(newAccessSchedules, oldExpire, newExpire);
    setValue('accessSettings.temporal.accessSchedules', newAccessSchedules);
  };

  function compareTags(current: string[], previous: string[]) {
    let direction = 0;
    let diff = [] as string[];
    if (current.length < previous.length) {
      direction = -1;
      diff = previous.filter((tag) => !current.includes(tag));
    } else if (current.length > previous.length) {
      direction = 1;
      diff = current.filter((tag) => !previous.includes(tag));
    }

    return { diff, direction };
  }

  useEffect(() => {
    const debouncedUpdate = debounce(() => {
      const tagChange = compareTags(tags, previousTags);

      if (tagChange.direction > 0 || tagChange.direction < 0) {
        setPreviousTags(tags);
        const commonFromTags = itemUtils.extractCommonTags(tags);
        const mergedCommon = {
          ...commonFromTags,
          categories: common?.categories,
        };
        setValue('common', mergedCommon);
      }
    }, 300);

    debouncedUpdate();

    return () => {
      debouncedUpdate.cancel();
    };
  }, [tags]);

  const debouncedUpdateRef = useRef<any>(null);

  useEffect(() => {
    // Create a debounced function
    debouncedUpdateRef.current = debounce((commonData) => {
      const commonTagsArray = itemUtils.getFullTagsFromCommonMetadata(commonData);
      const extractedTagsObj = itemUtils.extractCommonTags(tags);
      const basicTags = extractedTagsObj.tags;
      const extractedTags = [] as string[];
      Object.keys(extractedTagsObj).forEach((key) => {
        if (key !== 'tags') {
          extractedTags.push(...extractedTagsObj[key]);
        }
      });

      const commonTagsChange = compareTags(commonTagsArray, extractedTags);
      if (commonTagsChange.direction > 0 || commonTagsChange.direction < 0) {
        const newTags = Array.from(new Set([...basicTags, ...commonTagsArray]));
        setValue('tags', newTags);
        setPreviousTags(newTags);
      }
    }, 300);

    const subscription = watch((value, { name }) => {
      if (name && name.startsWith('common.')) {
        const commonData = value.common;
        if (debouncedUpdateRef.current) {
          debouncedUpdateRef.current(commonData);
        }
      }
    });
    return () => {
      subscription.unsubscribe();
      debouncedUpdateRef.current?.cancel?.();
    };
  }, [common]);

  return (
    <div className="form">
      <div className="form-grid" style={{ marginBottom: '1em' }}>
        <VNTextField name="schemaType" label="Media Type" disabled />
        <VNAutocomplete
          name="common.categories"
          label="Categories"
          multiple
          options={categories}
          labelProp="name"
          valueProp="id"
        />
        <VNTextField name="title" label="Title" disabled={isMultiple} />
        <FuelTags id="series-tags" inputTagWidth={300} tags={watch('tags') ?? []} onChange={(value) => setValue('tags', value)} />
        <VNDatePicker
          name="publicationDate"
          label={`Published (${ouData.timezoneAbbreviation})`}
          timezone={ouData.timezone}
          rules={{ required: 'Published Date is required' }}
          required
        />
        <div className="sub-row">
          <VNCheckBox name="applyAutomatedTransitions" label="Include Automated Transitions" />
          <VNCheckBox name="doNotDelete" label="Retain/Do Not Delete" />
        </div>
        <VNTextField name="synopsis" label="Description" rows={4} disabled={isMultiple} />
        <div className="control-group">
          <div className="sub-row">
            <VNDatePicker
              name="embargoDate"
              label={`Embargo (${ouData.timezoneAbbreviation})`}
              timezone={ouData.timezone}
              onDateChanged={onEmbargoDateChanged}
            />
            <VNDatePicker
              name="expireDate"
              label={`Expires (${ouData.timezoneAbbreviation})`}
              timezone={ouData.timezone}
              onDateChanged={onExpireDateChanged}
            />
          </div>

          {hasMov && (
            <div className="mov-checkbox">
              <VNCheckBox name="includeAsTransition" label="Include .mov(s) as Transition(s)" />
            </div>
          )}
        </div>
      </div>
      <Metadata isMultiple={isMultiple} isNew={isNew} />
    </div>
  );
}

export default VideoForm;
